import { AccessTime, Check } from "@mui/icons-material";
import { Box, Tooltip } from "@mui/material";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticTimePicker } from "@mui/x-date-pickers/StaticTimePicker";
import { DateTime } from "luxon";
import { useEffect, useRef, useState } from "react";
import { useModals } from "../../context/ModalContext";
import { BaseModalComponent } from "./BaseModalComponent";
import { IconButtonComponent } from "./IconButtonComponent";

interface PreciseTimePickerProps {
  iconColor?: "grey" | "orange" | "colorized";
  value: DateTime;
  onChange: (newTime: DateTime) => void;
  modalKey?: string;
  disabled: boolean;
  tooltipTitle?: string;
  buttonSize?: { width: number; height: number };
  minutesStep?: number;
}

export const PreciseTimePicker = ({
  iconColor = "grey",
  value,
  onChange,
  disabled = false,
  modalKey = `precise-time-picker-${Date.now()}`,
  tooltipTitle = "Set a more precise time",
  buttonSize = { width: 32, height: 32 },
  minutesStep,
}: PreciseTimePickerProps) => {
  const [tempTime, setTempTime] = useState<DateTime>(value);
  const [wasModalOpen, setWasModalOpen] = useState(false);
  const { closeByKey, modals } = useModals();
  // Keep ref to always access current tempTime value (avoids stale closures)
  const tempTimeRef = useRef<DateTime>(value);

  // Handle modal open/close and initialize tempTime when modal opens
  useEffect(() => {
    const modalIsOpen = modals.some((m) => m.key === modalKey && !m.minimized);

    if (modalIsOpen && !wasModalOpen) {
      // Modal just opened: initialize tempTime with current value
      tempTimeRef.current = value;
      setTempTime(value);
      setWasModalOpen(true);
    } else if (!modalIsOpen && wasModalOpen) {
      // Modal just closed
      setWasModalOpen(false);
    }
    // Note: 'value' is intentionally not in dependencies to prevent tempTime
    // from updating when user changes Select while modal is open
  }, [modals, modalKey, wasModalOpen]);

  const handleTimePickerChange = (newTime: DateTime | null) => {
    if (newTime) {
      tempTimeRef.current = newTime;
      setTempTime(newTime);
    }
  };

  const handleConfirmTime = () => {
    const currentTempTime = tempTimeRef.current;
    if (currentTempTime.isValid) {
      onChange(currentTempTime);
      closeByKey(modalKey);
    }
  };

  return (
    <BaseModalComponent
      modalLabel="Precise time picker"
      modalKey={modalKey}
      elementTrigger={
        <Tooltip
          title={tooltipTitle}
          arrow
          placement="top"
          PopperProps={{
            sx: { zIndex: 999999 },
          }}
        >
          <div style={{ display: "inline-flex" }}>
            <IconButtonComponent
              disabled={disabled}
              onClick={() => {}}
              theme={iconColor}
              sx={{ width: buttonSize.width, height: buttonSize.height }}
              sxIcon={{ fontSize: "small" }}
            >
              <AccessTime />
            </IconButtonComponent>
          </div>
        </Tooltip>
      }
      defaultSizes={{
        width: 450,
        height: "520px", //TODO: Make it responsive and auto, also read height with just raw integer number and not string px
      }}
      defaultFullscreen={false}
    >
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <Box sx={{ p: 2 }}>
          <StaticTimePicker
            value={tempTime}
            onChange={handleTimePickerChange}
            ampm
            minutesStep={minutesStep}
            slotProps={{ actionBar: { actions: [] } }}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <IconButtonComponent
              onClick={handleConfirmTime}
              theme="colorized"
              sx={{ width: buttonSize.width, height: buttonSize.height }}
              sxIcon={{ fontSize: "small" }}
              disabled={!tempTime.isValid}
            >
              <Check fontSize="small" />
            </IconButtonComponent>
          </Box>
        </Box>
      </LocalizationProvider>
    </BaseModalComponent>
  );
};
